import { useParams, Link, Navigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { usePageMeta } from '../hooks/usePageMeta';
import { getCityBySlug, CITIES } from '../data/cities';

const API_BASE = import.meta.env.VITE_API_URL
  ? `https://${import.meta.env.VITE_API_URL}`
  : 'https://api.helpped.com';

interface ArticleSummary {
  slug: string;
  title: string;
  published_at: string | null;
  article_type: string;
}

const TESTFLIGHT_URL = 'https://testflight.apple.com/join/helpped';

export default function CityPage() {
  const { slug } = useParams<{ slug: string }>();
  const city = getCityBySlug(slug ?? '');

  const [articles, setArticles] = useState<ArticleSummary[]>([]);
  useEffect(() => {
    if (!city) return;
    fetch(`${API_BASE}/api/cities/${city.slug}/council?limit=4`)
      .then((r) => r.ok ? r.json() : [])
      .then((d) => Array.isArray(d) && setArticles(d.slice(0, 4)))
      .catch(() => {});
  }, [city?.slug]);

  if (!city) return <Navigate to="/" replace />;

  usePageMeta(
    `${city.name}, WA Civic & Neighborhood App | Helpped`,
    city.metaDescription,
    { placename: `${city.name}, Washington`, lat: city.lat, lon: city.lon },
  );

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home',     item: 'https://helpped.com/' },
      { '@type': 'ListItem', position: 2, name: 'Coverage', item: 'https://helpped.com/#coverage' },
      { '@type': 'ListItem', position: 3, name: city.name,  item: `https://helpped.com/cities/${city.slug}` },
    ],
  };

  const webPageSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: `${city.name}, WA Civic & Neighborhood App | Helpped`,
    url: `https://helpped.com/cities/${city.slug}`,
    description: city.metaDescription,
    about: {
      '@type': 'City',
      name: city.name,
      containedInPlace: { '@type': 'State', name: 'Washington', addressCountry: 'US' },
      geo: { '@type': 'GeoCoordinates', latitude: city.lat, longitude: city.lon },
    },
    publisher: { '@id': 'https://helpped.com/#organization' },
  };

  const appSchema = {
    '@context': 'https://schema.org',
    '@type': 'MobileApplication',
    name: 'Helpped',
    operatingSystem: 'iOS',
    applicationCategory: 'NewsApplication',
    offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
    description: city.metaDescription,
    areaServed: {
      '@type': 'City',
      name: city.name,
      addressRegion: 'WA',
      addressCountry: 'US',
      geo: { '@type': 'GeoCoordinates', latitude: city.lat, longitude: city.lon },
    },
  };

  return (
    <>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify([breadcrumbSchema, webPageSchema, appSchema]) }} />

      <div className="min-h-screen flex flex-col bg-white">

        {/* ── Nav ── */}
        <nav className="sticky top-0 z-50 bg-white/90 backdrop-blur border-b border-gray-100">
          <div className="max-w-5xl mx-auto px-6 h-14 flex items-center gap-2 text-sm">
            <Link to="/" className="flex items-center gap-2 group">
              <div className="w-7 h-7 rounded-lg flex items-center justify-center text-white font-bold text-sm"
                style={{ background: 'hsl(var(--primary))' }}>H</div>
              <span className="font-bold text-gray-900 group-hover:opacity-80 transition-opacity">Helpped</span>
            </Link>
            <span className="text-gray-300 mx-1">/</span>
            <Link to="/#coverage" className="text-gray-500 hover:text-gray-800 transition-colors">Coverage</Link>
            <span className="text-gray-300 mx-1">/</span>
            <span className="text-gray-700 font-medium">{city.name}</span>

            <div className="ml-auto">
              <a href={TESTFLIGHT_URL} target="_blank" rel="noopener noreferrer"
                className="text-sm font-semibold text-white px-4 py-1.5 rounded-full hover:opacity-90 transition-opacity"
                style={{ background: 'hsl(var(--primary))' }}>
                Get the app
              </a>
            </div>
          </div>
        </nav>

        {/* ── Hero ── */}
        <section className="max-w-5xl mx-auto px-6 pt-14 pb-10 w-full">
          <div className="inline-flex items-center gap-2 text-xs font-semibold px-3 py-1.5 rounded-full mb-5 border"
            style={{ background: 'hsl(158 68% 95%)', borderColor: 'hsl(158 68% 80%)', color: 'hsl(158 68% 28%)' }}>
            <span className="w-1.5 h-1.5 rounded-full bg-current animate-pulse" />
            {city.county} · Washington State
          </div>

          <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-gray-900 leading-tight mb-4">
            {city.heroHeadline}
          </h1>

          <p className="text-lg text-gray-500 leading-relaxed max-w-2xl mb-8">
            {city.heroParagraph}
          </p>

          <a href={TESTFLIGHT_URL} target="_blank" rel="noopener noreferrer"
            className="inline-flex items-center gap-3 px-6 py-3.5 rounded-xl font-semibold text-white shadow-lg transition-transform hover:scale-105 active:scale-95 text-sm"
            style={{ background: '#000' }}>
            <svg width="16" height="20" viewBox="0 0 814 1000" fill="white">
              <path d="M788.1 340.9c-5.8 4.5-108.2 62.2-108.2 190.5 0 148.4 130.3 200.9 134.2 202.2-.6 3.2-20.7 71.9-68.7 141.9-42.8 61.6-87.5 123.1-155.5 123.1s-85.5-39.5-164-39.5c-76 0-103.7 40.8-165.9 40.8s-105-57.8-155.5-127.4C46 384.7 8.1 191.3 8.1 106.2c0-134.9 87.6-206.1 173.4-206.1 46.9 0 85.9 30.7 114.8 30.7 27.5 0 71.5-32.7 123.1-32.7 20.8 0 108.2 1.9 160.8 67.1zm-138.9-141.9C617.1 138.5 651.9 89 651.9 39.5c0-6.4-.7-12.8-1.3-19.3-47.3 1.9-101.7 31.3-134.2 71.6-29.4 35.2-57.8 86.4-57.8 135.7 0 6.4.7 12.8 1.3 19.3 4.5.7 9 1.3 13.5 1.3 44.2 0 95.9-27.5 127.8-69.8z"/>
            </svg>
            Download on TestFlight — Free
          </a>
        </section>

        {/* ── Stats ── */}
        <section className="border-y border-gray-100" style={{ background: 'hsl(158 68% 97%)' }}>
          <div className="max-w-5xl mx-auto px-6 grid grid-cols-3 divide-x divide-gray-200">
            <div className="py-6 text-center">
              <p className="text-3xl sm:text-4xl font-extrabold text-gray-900">{city.eventsTracked.toLocaleString()}+</p>
              <p className="text-xs font-semibold text-gray-500 mt-1">Civic events tracked</p>
            </div>
            <div className="py-6 text-center">
              <p className="text-3xl sm:text-4xl font-extrabold text-gray-900">{city.placesIndexed.toLocaleString()}+</p>
              <p className="text-xs font-semibold text-gray-500 mt-1">Local places mapped</p>
            </div>
            <div className="py-6 text-center">
              <p className="text-3xl sm:text-4xl font-extrabold text-gray-900">Free</p>
              <p className="text-xs font-semibold text-gray-500 mt-1">iOS app via TestFlight</p>
            </div>
          </div>
        </section>

        {/* ── Civic context ── */}
        <section className="max-w-5xl mx-auto px-6 py-12 w-full grid md:grid-cols-2 gap-10">
          <div>
            <h2 className="text-xl font-bold text-gray-900 mb-3">
              How {city.name} civic coverage works
            </h2>
            <p className="text-sm text-gray-600 leading-relaxed mb-4">{city.civicContext}</p>
            <p className="text-sm text-gray-600 leading-relaxed">
              Council data is pulled from{' '}
              <strong>{city.councilPlatform}</strong> — {city.name}'s official meeting management
              platform — and updated within hours of publication. Every agenda item, vote record,
              and resolution is translated into plain language before it reaches your feed.
            </p>
            <a href={city.councilUrl} target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 mt-4 text-sm font-semibold hover:opacity-80 transition-opacity"
              style={{ color: 'hsl(var(--primary))' }}>
              View {city.name} City Council →
            </a>
          </div>

          <div>
            <h2 className="text-xl font-bold text-gray-900 mb-3">
              Key civic topics in {city.name}
            </h2>
            <ul className="space-y-3">
              {city.keyTopics.map((topic) => (
                <li key={topic} className="flex items-start gap-2.5 text-sm text-gray-600">
                  <span className="mt-0.5 w-4 h-4 rounded-full shrink-0 flex items-center justify-center text-white text-xs font-bold"
                    style={{ background: 'hsl(var(--primary))' }}>✓</span>
                  {topic}
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* ── Recent council coverage ── */}
        {articles.length > 0 && (
          <section className="max-w-5xl mx-auto px-6 pb-12 w-full">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-lg font-bold text-gray-900">
                Recent {city.name} council coverage
              </h2>
              <Link to={`/cities/${city.slug}/council`}
                className="text-sm font-semibold hover:opacity-80 transition-opacity"
                style={{ color: 'hsl(var(--primary))' }}>
                View all →
              </Link>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              {articles.map((a) => (
                <Link key={a.slug} to={`/cities/${city.slug}/council/${a.slug}`}
                  className="p-4 rounded-xl border border-gray-100 bg-white shadow-sm hover:shadow-md transition-shadow group">
                  <span className="text-xs font-semibold px-2 py-0.5 rounded-full mb-2 inline-block"
                    style={{
                      background: a.article_type === 'meeting' ? 'hsl(38 90% 94%)' : 'hsl(158 68% 94%)',
                      color: a.article_type === 'meeting' ? 'hsl(38 60% 35%)' : 'hsl(158 68% 28%)',
                    }}>
                    {a.article_type === 'meeting' ? 'Meeting' : 'Council Vote'}
                  </span>
                  <p className="text-sm font-semibold text-gray-900 group-hover:text-green-700 transition-colors leading-snug">
                    {a.title}
                  </p>
                </Link>
              ))}
            </div>
          </section>
        )}

        {/* ── Resources link ── */}
        <section className="max-w-5xl mx-auto px-6 pb-12 w-full">
          <Link to={`/cities/${city.slug}/resources`}
            className="block p-5 rounded-2xl border border-gray-100 bg-white shadow-sm hover:shadow-md transition-shadow group">
            <div className="flex items-center justify-between gap-4">
              <div>
                <h2 className="text-lg font-bold text-gray-900 group-hover:text-green-700 transition-colors">
                  Community resources in {city.name}
                </h2>
                <p className="text-sm text-gray-500 mt-1">
                  Food banks, shelters, free clinics, and social services — hours, eligibility, and how to get help.
                </p>
              </div>
              <span className="shrink-0 text-2xl" style={{ color: 'hsl(var(--primary))' }}>→</span>
            </div>
          </Link>
        </section>

        {/* ── Mid-page CTA — geo-specific, highest intent on city pages ── */}
        <section className="px-6 py-12" style={{ background: 'hsl(158 68% 95%)' }}>
          <div className="max-w-5xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-6">
            <div>
              <p className="text-lg font-bold text-gray-900 mb-1">
                Track {city.name} city council on your phone
              </p>
              <p className="text-sm text-gray-600">
                {city.eventsTracked}+ civic events indexed · {city.placesIndexed.toLocaleString()} local places mapped · Free on iOS
              </p>
            </div>
            <a
              href={TESTFLIGHT_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="shrink-0 inline-flex items-center gap-3 px-6 py-3.5 rounded-xl font-semibold text-white shadow-md transition-transform hover:scale-105 active:scale-95 text-sm"
              style={{ background: '#000' }}
            >
              <svg width="16" height="20" viewBox="0 0 814 1000" fill="white">
                <path d="M788.1 340.9c-5.8 4.5-108.2 62.2-108.2 190.5 0 148.4 130.3 200.9 134.2 202.2-.6 3.2-20.7 71.9-68.7 141.9-42.8 61.6-87.5 123.1-155.5 123.1s-85.5-39.5-164-39.5c-76 0-103.7 40.8-165.9 40.8s-105-57.8-155.5-127.4C46 384.7 8.1 191.3 8.1 106.2c0-134.9 87.6-206.1 173.4-206.1 46.9 0 85.9 30.7 114.8 30.7 27.5 0 71.5-32.7 123.1-32.7 20.8 0 108.2 1.9 160.8 67.1zm-138.9-141.9C617.1 138.5 651.9 89 651.9 39.5c0-6.4-.7-12.8-1.3-19.3-47.3 1.9-101.7 31.3-134.2 71.6-29.4 35.2-57.8 86.4-57.8 135.7 0 6.4.7 12.8 1.3 19.3 4.5.7 9 1.3 13.5 1.3 44.2 0 95.9-27.5 127.8-69.8z"/>
              </svg>
              Get Helpped — Free on iOS
            </a>
          </div>
        </section>

        {/* ── Other covered cities ── */}
        <section className="border-t border-gray-100 py-10 px-6" style={{ background: 'hsl(30 20% 98%)' }}>
          <div className="max-w-5xl mx-auto">
            <h2 className="text-sm font-bold uppercase tracking-widest text-gray-400 mb-5">
              Also covered by Helpped
            </h2>
            <div className="flex flex-wrap gap-2">
              {CITIES.filter((c) => c.slug !== city.slug).map((c) => (
                <Link key={c.slug} to={`/cities/${c.slug}`}
                  className="text-sm font-medium px-3 py-1.5 rounded-full border border-gray-200 bg-white text-gray-600 hover:border-gray-400 hover:text-gray-900 transition-colors">
                  {c.name}
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* ── Footer ── */}
        <footer className="px-6 py-8 border-t border-gray-100">
          <div className="max-w-5xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-gray-400">
            <span>© {new Date().getFullYear()} Helpped — Washington State</span>
            <div className="flex gap-6">
              <Link to="/" className="hover:text-gray-600 transition-colors">Home</Link>
              <Link to="/privacy" className="hover:text-gray-600 transition-colors">Privacy</Link>
              <Link to="/terms" className="hover:text-gray-600 transition-colors">Terms</Link>
              <a href="mailto:hello@helpped.com" className="hover:text-gray-600 transition-colors">Contact</a>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
}
